import { useEffect, useState } from 'react'

import { usePermissions } from '@/hooks/usePermissions'
import { useTranslation } from 'react-i18next'

import {
  AddIcon,
  Checkbox,
  Box as DarkBox,
  IconButton,
  RemoveIcon,
  TextInput,
} from '@hivewatch/dark'

import { CheckpointTaskInputI } from '../types/types'
import { formatItemsForParent } from '../utils/utils'
import { darkCheckPointsPanelStyles } from './DarkCheckPointsPanel.styles'

const MAX_CHECKLIST_LENGTH = 5
const MAX_CHARACTER_LENGTH = 100

interface DarkCheckpointChecklistIProps {
  colorScheme: 'dark' | 'light'
  existingValues: { value: string; id: string }[]
  hasDuplicateItems: boolean
  isInEditMode: boolean
  areTasksRequired?: boolean
  onRequiredChange?: (required: boolean) => void
  onUpdate: (items: CheckpointTaskInputI[]) => void
}

/**
 * Dark twin of CheckpointChecklist. Same row state and parent contract; the
 * controls come from the dark package so the item inputs stay readable on the
 * dark surface, where the legacy Chakra v1 Input rendered its text invisible.
 */
export const DarkCheckpointChecklist = ({
  colorScheme,
  existingValues,
  hasDuplicateItems,
  isInEditMode,
  areTasksRequired = false,
  onRequiredChange,
  onUpdate,
}: DarkCheckpointChecklistIProps) => {
  const { t } = useTranslation()
  const { shouldEnableCheckpointChecklist } = usePermissions()
  const [rows, setRows] = useState(existingValues)
  const [isInitialized, setIsInitialized] = useState(false)
  const isDisabled = !isInEditMode || !shouldEnableCheckpointChecklist

  const addRow = () => {
    setRows([...rows, { id: `temp-${Math.random()}`, value: '' }])
  }

  // Initialize rows from existingValues only once
  useEffect(() => {
    if (!isInitialized && existingValues.length >= 0) {
      setRows(existingValues)
      setIsInitialized(true)
    }
  }, [existingValues, isInitialized])

  // Call onUpdate when rows change
  useEffect(() => {
    onUpdate(formatItemsForParent(rows))
  }, [rows])

  const removeRow = (id: string) => {
    setRows(rows.filter((row) => row.id !== id))
  }

  const updateRow = (id: string, value: string) => {
    setRows(rows.map((row) => (row.id === id ? { ...row, value } : row)))
  }

  const s = darkCheckPointsPanelStyles(colorScheme)

  return (
    <DarkBox {...s.checklist}>
      <DarkBox {...s.checklistHeader}>
        <DarkBox {...s.checklistTitle}>
          {t('floors:checkpoint_checklist_title')}
        </DarkBox>
        <IconButton
          aria-label={t('floors:checkpoint_add_checklist_item_aria')}
          colorScheme={colorScheme}
          data-testid='DarkEditCheckPoint:addChecklistItem'
          dimension={28}
          disabled={
            !isInEditMode ||
            rows.length === MAX_CHECKLIST_LENGTH ||
            !shouldEnableCheckpointChecklist
          }
          icon={<AddIcon size={10} />}
          onClick={addRow}
        />
      </DarkBox>

      <Checkbox
        checked={areTasksRequired}
        colorScheme={colorScheme}
        data-testid='DarkEditCheckPoint:checklistRequired'
        disabled={isDisabled}
        label={
          <DarkBox as='span' {...s.checklistLabel}>
            {t('floors:checkpoint_checklist_required')}
          </DarkBox>
        }
        onChange={(e) => onRequiredChange?.(e.target.checked)}
      />

      {rows.map((row) => (
        <DarkBox {...s.checklistRow} key={row.id}>
          <TextInput
            colorScheme={colorScheme}
            disabled={isDisabled}
            maxLength={MAX_CHARACTER_LENGTH}
            onValueChange={(value) => updateRow(row.id, value)}
            placeholder={t('floors:checkpoint_checklist_item_placeholder')}
            value={row.value}
            width='100%'
          />
          <IconButton
            aria-label={t('floors:checkpoint_remove_checklist_aria')}
            colorScheme={colorScheme}
            dimension={28}
            disabled={isDisabled}
            icon={<RemoveIcon size={14} />}
            onClick={() => removeRow(row.id)}
          />
        </DarkBox>
      ))}

      {hasDuplicateItems && (
        <DarkBox {...s.checklistError}>
          {t('floors:checkpoint_checklist_all_unique')}
        </DarkBox>
      )}

      {rows.length === 0 && (
        <DarkBox {...s.checklistEmpty}>
          {t('floors:checkpoint_checklist_empty')}
        </DarkBox>
      )}
    </DarkBox>
  )
}
